import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FacebookPhotoNotice } from './FacebookPhotoNotice';

interface ImageLightboxProps {
    mediaUrls: string[];
    facebookPostUrl?: string;
    startIndex: number;
    onClose: () => void;
}

export function ImageLightbox({ mediaUrls, facebookPostUrl, startIndex, onClose }: ImageLightboxProps) {
    const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), mediaUrls.length - 1));
    const [isLoading, setIsLoading] = useState(false);
    const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
    const url = mediaUrls[index];
    const cleanUrl = url?.split('?')[0].toLowerCase();
    const isVideo = cleanUrl?.endsWith('.mp4') || cleanUrl?.endsWith('.mov');
    const imgRef = useRef<HTMLImageElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const mediaMaxHeight = facebookPostUrl ? 'max-h-[calc(100dvh-14rem)]' : 'max-h-[86vh] sm:max-h-[88vh]';

    useEffect(() => {
        setIndex(Math.min(Math.max(startIndex, 0), mediaUrls.length - 1));
    }, [mediaUrls.length, startIndex]);

    const goToIndex = useCallback((newIndex: number) => {
        const clampedIndex = Math.min(Math.max(newIndex, 0), mediaUrls.length - 1);
        setIndex(clampedIndex);
    }, [index, mediaUrls.length]);
    const prev = useCallback(() => goToIndex(index - 1), [goToIndex, index]);
    const next = useCallback(() => goToIndex(index + 1), [goToIndex, index]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') next();
            else if (e.key === 'ArrowLeft') prev();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [next, onClose, prev]);

    // Preload adjacent images to eliminate paging lag
    useEffect(() => {
        const preloadUrls = mediaUrls.slice(Math.max(0, index - 1), Math.min(mediaUrls.length, index + 3));
        preloadUrls.forEach(url => {
            const cleanUrl = url.split('?')[0].toLowerCase();
            if (!cleanUrl.endsWith('.mp4') && !cleanUrl.endsWith('.mov')) {
                const img = new Image();
                img.src = url;
            }
        });
    }, [index, mediaUrls]);

    useEffect(() => {
        if (!url) return;
        const clean = url.split('?')[0].toLowerCase();

        // Fast synchronous cached check for images.
        if (!clean.endsWith('.mp4') && !clean.endsWith('.mov')) {
            const pre = new Image();
            pre.src = url;
            if (pre.complete && pre.naturalWidth) {
                setIsLoading(false);
                return;
            }
        }

        // Delay showing the spinner briefly to avoid flicker for ultra-fast loads.
        const showTimer = window.setTimeout(() => setIsLoading(true), 80);

        return () => {
            clearTimeout(showTimer);
        };
    }, [url]);

    // If the image/video is already cached/ready, clear the loading spinner.
    useEffect(() => {
        if (!url) return;
        let cancelled = false;
        requestAnimationFrame(() => {
            if (cancelled) return;
            if (isVideo) {
                const v = videoRef.current;
                if (v && (v.readyState >= 2 || v.readyState === 4)) {
                    setIsLoading(false);
                }
            } else {
                const img = imgRef.current;
                if (img && img.complete && img.naturalWidth) {
                    setIsLoading(false);
                }
            }
        });
        return () => { cancelled = true; };
    }, [url, isVideo]);

    if (mediaUrls.length === 0 || !url) return null;

    return (
        <div
            className={`fixed inset-0 z-[70] flex items-center justify-center bg-black ${facebookPostUrl ? 'pb-40 pt-16 sm:pb-28 sm:pt-20' : ''}`}
            onTouchStart={(e) => setTouchStart({
                x: e.changedTouches[0].clientX,
                y: e.changedTouches[0].clientY,
            })}
            onTouchEnd={(e) => {
                if (touchStart === null) return;
                const deltaX = touchStart.x - e.changedTouches[0].clientX;
                const deltaY = touchStart.y - e.changedTouches[0].clientY;
                if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
                    if (deltaX > 0) next();
                    else prev();
                }
                setTouchStart(null);
            }}
        >
            <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-3 py-3 text-white sm:px-6 sm:py-5">
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-10 items-center gap-2 rounded-full px-2 text-white hover:bg-white/10 sm:px-4"
                    aria-label="Close lightbox"
                >
                    <XMarkIcon className="h-6 w-6" />
                    <span className="hidden text-sm font-medium sm:inline">Close</span>
                </button>

                <div className="rounded-full bg-black/55 px-3 py-1 text-sm font-medium text-white">
                    {index + 1}/{mediaUrls.length}
                </div>
            </div>

            <div className={`relative flex min-w-48 max-w-[100vw] items-center justify-center px-4 sm:max-w-[88vw] sm:px-0 ${mediaMaxHeight} ${facebookPostUrl ? '' : 'min-h-48'}`}>
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    </div>
                )}

                {isVideo ? (
                    <video
                        key={url}
                        src={url}
                        ref={videoRef}
                        controls
                        autoPlay
                        muted
                        className={`${mediaMaxHeight} max-w-full object-contain shadow-2xl transition-opacity sm:max-w-[88vw] ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                        onClick={(e) => e.stopPropagation()}
                        onLoadedData={() => setIsLoading(false)}
                        onError={() => setIsLoading(false)}
                    />
                ) : (
                    <img
                        key={url}
                        src={url}
                        ref={imgRef}
                        alt={`Image ${index + 1} of ${mediaUrls.length}`}
                        className={`${mediaMaxHeight} max-w-full object-contain shadow-2xl transition-opacity sm:max-w-[88vw] ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                        onClick={(e) => e.stopPropagation()}
                        onLoad={() => setIsLoading(false)}
                        onError={() => setIsLoading(false)}
                    />
                )}
            </div>

            {index > 0 && (
                <button
                    type="button"
                    onClick={prev}
                    className="absolute left-6 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-950 shadow-xl transition hover:scale-105 hover:bg-gray-100 sm:flex"
                    aria-label="Previous photo"
                >
                    <ChevronLeftIcon className="h-7 w-7" />
                </button>
            )}

            {index < mediaUrls.length - 1 && (
                <button
                    type="button"
                    onClick={next}
                    className="absolute right-6 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-950 shadow-xl transition hover:scale-105 hover:bg-gray-100 sm:flex"
                    aria-label="Next photo"
                >
                    <ChevronRightIcon className="h-7 w-7" />
                </button>
            )}

            {facebookPostUrl && (
                <div className="absolute inset-x-0 bottom-0 z-20 px-4 pb-4 sm:px-6">
                    <FacebookPhotoNotice postUrl={facebookPostUrl} dark />
                </div>
            )}
        </div>
    );
}
